import { useEffect, useRef, useState } from "react";

/** How long one rising crown stays on screen — matches the CSS animation it triggers. */
const BURST_MS = 900;

/**
 * The stack of rising crowns a fap throws off.
 *
 * Every tap pushes another id, so rapid taps stack rather than restarting one animation,
 * and each id retires itself after `BURST_MS`. The ids are a monotonic counter purely so
 * React has a stable key per crown; nothing reads their value.
 *
 * Shared because both fap surfaces do exactly this — the operator's `control/FapBar` and
 * the wall's `display/RegionFapOverlay` — and they carried a byte-identical copy each,
 * comment included. The timers matter: a tile that unmounts mid-animation would otherwise
 * leave `setState` calls queued against a dead component, which is why the pending set is
 * cleared on unmount rather than left to fire.
 */
export function useCrownBursts(): { bursts: number[]; pushBurst: () => void } {
  const [bursts, setBursts] = useState<number[]>([]);
  const nextId = useRef(0);
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, []);

  const pushBurst = () => {
    const id = ++nextId.current;
    setBursts((b) => [...b, id]);
    const t = setTimeout(() => {
      setBursts((b) => b.filter((x) => x !== id));
      timers.current.delete(t);
    }, BURST_MS);
    timers.current.add(t);
  };

  return { bursts, pushBurst };
}
